import React, { useState } from "react";
import { Lock, Loader2, ShieldCheck } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Logo } from "@/components/Logo";

export default function ForcePasswordChange() {
  const { user, refreshUser } = useAuth();
  const [pw, setPw] = useState("");
  const [confirm, setConfirm] = useState("");
  const [saving, setSaving] = useState(false);

  if (!user || !user.must_change_password) return null;

  const submit = async () => {
    if (pw.length < 6) { toast.error("Password must be at least 6 characters"); return; }
    if (pw !== confirm) { toast.error("Passwords don't match"); return; }
    setSaving(true);
    try {
      const { data } = await api.post("/auth/set-new-password", { new_password: pw });
      if (data.access_token) localStorage.setItem("carivisor_token", data.access_token);
      await refreshUser();
      toast.success("Password updated — you're all set!");
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center px-4 bg-[#060810]" data-testid="force-password-change">
      <div className="glass neon-border rounded-2xl p-8 sm:p-10 max-w-md w-full">
        <div className="flex justify-center mb-6"><Logo size={40} tagline /></div>
        <div className="text-center mb-6">
          <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cyan-500 to-blue-600 flex items-center justify-center mx-auto mb-4"><Lock className="w-6 h-6 text-white" /></div>
          <h1 className="font-heading text-2xl font-bold text-white">Set a new password</h1>
          <p className="text-slate-400 text-sm mt-2">An admin issued you a temporary password. For your security, please choose a new one before continuing.</p>
        </div>
        <div className="space-y-4">
          <div>
            <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">New password</label>
            <input data-testid="force-new-password" type="password" value={pw} onChange={(e) => setPw(e.target.value)} placeholder="At least 6 characters"
              className="w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/60" />
          </div>
          <div>
            <label className="block text-xs font-mono-caption uppercase tracking-widest text-slate-400 mb-2">Confirm new password</label>
            <input data-testid="force-confirm-password" type="password" value={confirm} onChange={(e) => setConfirm(e.target.value)} placeholder="Re-enter new password"
              className="w-full bg-[#0B0E1B] border border-[#1D2640] rounded-xl px-4 py-3 text-sm text-white outline-none focus:border-cyan-400/60" />
          </div>
          <button data-testid="force-submit-btn" onClick={submit} disabled={saving}
            className="w-full btn-neon rounded-xl px-6 py-3 font-semibold flex items-center justify-center gap-2 disabled:opacity-60">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <ShieldCheck className="w-4 h-4" />} Set password & continue
          </button>
        </div>
      </div>
    </div>
  );
}
